/**
 * @prop --component-container-primary-color-background: background color of "container primary"
 * @prop --component-container-primary-color: color of "container primary"
 * @prop --component-container-primary-color-light-background: background color of "container primary light"
 * @prop --component-container-primary-color-light: color of "container primary light"
 * @prop --component-container-secondary-color-background: background color of "container secondary"
 * @prop --component-container-secondary-color: color of "container secondary"
 * @prop --component-container-outline-color-background: background color of "container outline"
 * @prop --component-container-outline-color-font: font color of "container outline"
 * @prop --component-container-outline-color-border: outline color of "container outline"
 * @prop --component-container-white-color-background: background color of "container white"
 * @prop --component-container-white-color-font: font color of "container white"
 * @prop --component-container-outline-border-width: border width of "container outline"
 */

$border-width: 1px;

:host {
  display: block;
  overflow: hidden;
  font-family: var(--component-container-font-family, var(--skin-font-family-primary, $font-family-sans-serif));

  .container {
    background-color: var(--internal-color-background, $ui-white);
    color: var(--internal-color, $ui-dark);
    border-color: var(--internal-border-color, $ui-dark);
    border-width: 0;
    border-style: solid;
    box-sizing: border-box;
    height: 100%;
    padding: 17px 20px;

    @include resetCurrentColors();

    @include breakpoint($medium) {
      padding: 37px 40px;
    }

    --component-container-outline-border-width: #{$border-width};

    &--primary,
    &--primary::before,
    &--primary .container--stylized__background {
      --internal-color-background:
        var(
          --component-container-primary-color-background,
          var(--skin-color-primary, #{$ui-blue})
        );
      --internal-color: var(--component-container-primary-color, var(--skin-color-primary-inverted, #{$ui-white}));

      @include invertCurrentColors();
    }

    &--primary-lighter,
    &--primary-lighter::before,
    &--primary-lighter .container--stylized__background {
      --internal-color-background:
        var(
          --component-container-primary-color-lighter-background,
          var(--skin-color-primary-lighter, #{$ui-blue-minus-5})
        );
      --internal-color: var(--component-container-primary-color, #{$ui-dark});
    }

    &--secondary-lighter,
    &--secondary-lighter::before,
    &--secondary-lighter .container--stylized__background {
      --internal-color-background:
        var(
          --component-container-secondary-color-lighter-background,
          var(--skin-color-secondary-lighter, #{$ui-grey-minus-3})
        );
      --internal-color: var(--component-container-secondary-color, #{$ui-dark});
    }

    &--secondary-light,
    &--secondary-light::before,
    &--secondary-light .container--stylized__background {
      --internal-color-background:
        var(
          --component-container-secondary-color-light-background,
          var(--skin-color-secondary-light, #{$ui-grey-minus-2})
        );
      --internal-color: var(--component-container-secondary-color, #{$ui-dark});
    }

    &--white,
    &--white::before,
    &--white .container--stylized__background {
      --internal-color-background:
        var(
          --component-container-white-color-background,
          var(--skin-color-white, #{$ui-white})
        );
      --internal-color: var(--component-container-white-color-font, var(--skin-color-text, #{$ui-dark}));
    }

    &--white-outline-dark,
    &--white-outline-dark::before,
    &--white-outline-dark .container--stylized__background {
      --internal-color-background:
        var(
          --component-container-white-color-background,
          var(--skin-color-white, #{$ui-white})
        );
      --internal-color: var(--component-container-white-color-font, var(--skin-color-text, #{$ui-dark}));
      --internal-border-color: var(--skin-color-secondary, #{$ui-grey});
      --internal-border-width: var(--component-container-outline-border-width, #{$border-width});

      border-width: var(--internal-border-width);
    }

    &--outline-white,
    &--outline-white::before,
    &--outline-white .container--stylized__background {
      --internal-color-background: none;
      --internal-border-color: var(--skin-color-white, #{$ui-white});
      --internal-border-width: var(--component-container-outline-border-width, #{$border-width});

      border-width: var(--internal-border-width);
    }

    &--outline-white-dark,
    &--outline-white-dark::before,
    &--outline-white-dark .container--stylized__background {
      --internal-color-background: none;
      --internal-color: var(--component-container-primary-color, var(--skin-color-primary-inverted, #{$ui-white}));
      --internal-border-color: var(--skin-color-white, #{$ui-white});
      --internal-border-width: var(--component-container-outline-border-width, #{$border-width});

      border-width: var(--internal-border-width);

      @include invertCurrentColors();
    }

    &--outline-primary,
    &--outline-primary::before,
    &--outline-primary .container--stylized__background {
      --internal-color-background: none;
      --internal-border-color: var(--component-container-outline-color-border, var(--skin-color-primary, #{$ui-blue}));
      --internal-border-width: var(--component-container-outline-border-width, #{$border-width});

      border-width: var(--internal-border-width);
    }

    &--outline-accent,
    &--outline-accent::before,
    &--outline-accent .container--stylized__background {
      --internal-color-background: none;
      --internal-border-color: var(--component-container-outline-color-border, var(--skin-color-accent, #{$ui-green}));
      --internal-border-width: var(--component-container-outline-border-width, #{$border-width});

      border-width: var(--internal-border-width);
    }

    &--fullwidth {
      margin-left: -4.85%;
      margin-right: -4.85%;
      padding: 17px 4.85%;

      @include breakpoint($small) {
        padding: 17px 4.85%;
      }

      @include breakpoint($medium) {
        padding: 37px 4.85%;
      }

      @include breakpoint($xxlarge) {
        margin-left: calc((100vw - 1200px) / -2);
        margin-right: calc((100vw - 1200px) / -2);
        padding: 37px calc((100vw - 1200px) / 2);
      }
    }

    &--stylized {
      background-color: transparent;
      padding-bottom: calc(21.256% + 17px);
      padding-top: calc(21.256% + 17px);
      position: relative;
      z-index: 1;
      border-width: 0;

      @include breakpoint($medium) {
        padding-bottom: calc(21.256% + 36px);
        padding-top: calc(21.256% + 36px);
      }

      .container--stylized__background,
      &::before {
        background-color: var(--internal-color-background, $ui-white);
        color: var(--internal-color, $ui-dark);
        border-color: var(--internal-border-color, $ui-dark);
        border-width: var(--internal-border-width, 0);
        border-style: solid;
        border-radius: 0 60px;
        bottom: 0;
        content: '';
        left: 0;
        margin-bottom: calc(21.256% / 2);
        margin-top: calc(21.256% / 2);
        position: absolute;
        right: 0;
        top: 0;
        transform: skewY(-11deg);
        z-index: -1;

        @include breakpoint($medium) {
          border-radius: 0 72px;
        }

        @include breakpoint($desktop) {
          border-radius: 0 84px;
        }
      }

      &.container--opaque {
        &::before,
        .container--stylized__background {
          opacity: .85;
        }
      }

      &.container--outline {
        border-color: transparent;
      }

      &.container--fullwidth {
        opacity: 0;
        padding-bottom: 0;
        padding-top: 0;
        transform: translateY(-6px);

        // reset default stylized background
        &::before {
          display: none;
        }

        &::before,
        .container--stylized__background {
          margin: 0;
        }

        &.container--stylized--initialized {
          opacity: 1;
        }
      }
    }

    &--no-padding {
      padding: 0;
    }
  }
}

:host([full-width]) {
  overflow: visible;
}
